import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchProfilesByIds } from '@/utils/fetchProfilesByIds';
import { insertVolunteerNotification } from '@/utils/volunteerNotificationInsert';
import { assignVolunteerShifts, fetchEventShifts } from '@/utils/eventShiftsApi';
import { compareShifts, formatShiftLabel, isShiftFull, type EventShift } from '@/utils/eventShifts';

function createVolunteerRowId(): string {
  // DB expects a non-null id; generate one client-side.
  // (Most browsers support crypto.randomUUID; keep a small fallback.)
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

function formatDepartmentsForDb(departments: string[] | undefined): string | null {
  if (!departments?.length) return null;
  return departments.map((d) => d.trim()).filter(Boolean).join(', ') || null;
}

interface ApplicantProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  profile_image: string | null;
}

export interface Applicant {
  id: string;
  name: string;
  role: string;
  image: string | null;
  volunteer_id: string;
  event_id: string;
  application_id: string;
  departments: string[];
  /** Shifts the applicant picked (volunteer_applications.selected_shift_ids). */
  shiftIds: string[];
  event: string;
  answers: { question: string; answer: string }[];
}

export interface UseWaitingListOptions {
  onAcceptError?: (message: string) => void;
  /** The applicant was accepted, but not onto every shift they picked. */
  onAcceptWarning?: (message: string) => void;
}

/** The picked shifts as a readable answer, full ones marked. Deleted shifts are skipped. */
function describePickedShifts(shiftIds: string[], shiftsById: Map<string, EventShift>): string {
  const picked = shiftIds.flatMap((id) => {
    const shift = shiftsById.get(id);
    return shift ? [shift] : [];
  });
  if (picked.length === 0) return 'None';
  return picked
    .sort(compareShifts)
    .map((shift) => `${formatShiftLabel(shift)}${isShiftFull(shift) ? ' (full)' : ''}`)
    .join('\n');
}

/**
 * Put an accepted applicant on the shifts they picked. Capacity is checked in the database,
 * so a shift that filled up since they applied is skipped rather than overfilled.
 * Returns a message when not every pick could be assigned; acceptance itself stands.
 */
async function assignApplicantShifts(applicant: Applicant): Promise<string | null> {
  if (applicant.shiftIds.length === 0) return null;
  const result = await assignVolunteerShifts(applicant.event_id, applicant.volunteer_id, applicant.shiftIds);
  if (!result.ok) {
    return `${applicant.name} was accepted, but their shifts could not be assigned. You can set them from the volunteer list.`;
  }
  const full = result.full.length;
  if (full === 0) return null;
  return `${applicant.name} was accepted, but ${full === 1 ? '1 shift they picked was' : `${full} shifts they picked were`} already full.`;
}

async function upsertVolunteerFromApplicant(
  applicant: Applicant,
): Promise<{ ok: true; notifyVolunteer: boolean } | { ok: false; message: string }> {
  const department = formatDepartmentsForDb(applicant.departments);

  const { data: existing, error: existingErr } = await supabase
    .from('volunteers')
    .select('id, is_active')
    .eq('volunteer_id', applicant.volunteer_id)
    .eq('event_id', applicant.event_id)
    .maybeSingle();

  if (existingErr) {
    return { ok: false, message: existingErr.message || 'Could not check existing volunteer' };
  }

  if (existing) {
    if (existing.is_active) {
      return { ok: true, notifyVolunteer: false };
    }
    const { error: updateErr } = await supabase
      .from('volunteers')
      .update({
        is_active: true,
        department,
      })
      .eq('id', existing.id);
    if (updateErr) {
      return { ok: false, message: updateErr.message || 'Failed to reactivate volunteer' };
    }
    return { ok: true, notifyVolunteer: true };
  }

  const { error: insertErr } = await supabase.from('volunteers').insert({
    id: createVolunteerRowId(),
    volunteer_id: applicant.volunteer_id,
    event_id: applicant.event_id,
    hours: 0,
    is_active: true,
    department,
  });

  if (insertErr) {
    return { ok: false, message: insertErr.message || 'Failed to add volunteer' };
  }
  return { ok: true, notifyVolunteer: true };
}

export function useWaitingList(
  selectedEventId: string | null,
  userId: string | undefined,
  activeTab: string,
  refetch: () => void,
  options?: UseWaitingListOptions,
) {
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const selectedEventIdRef = useRef(selectedEventId);
  selectedEventIdRef.current = selectedEventId;

  useEffect(() => {
    setApplicants([]);
  }, [selectedEventId]);

  const fetchVolunteerApplications = useCallback(async () => {
    const requestedEventId = selectedEventId;
    if (!requestedEventId || !userId) {
      setApplicants([]);
      return;
    }
    setLoadingApplicants(true);
    try {
      const { data: applications, error: appErr } = await supabase
        .from('volunteer_applications')
        .select('*, event:events(id, name)')
        .eq('event_id', requestedEventId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (appErr) {
        setApplicants([]);
        return;
      }

      const volunteerIds = applications?.map((a: { volunteer_id: string }) => a.volunteer_id) || [];
      const [{ profilesById }, shifts] = await Promise.all([
        fetchProfilesByIds<ApplicantProfile>(volunteerIds, 'id, first_name, last_name, profile_image', 'useWaitingList'),
        fetchEventShifts(requestedEventId),
      ]);
      const shiftsById = new Map((shifts ?? []).map((shift) => [shift.id, shift]));

      type ApplicationRow = {
        id: string;
        volunteer_id: string;
        event_id: string;
        created_at: string;
        selected_departments: string[] | null;
        selected_shift_ids: string[] | null;
        selected_time_slots: string[] | null;
        event: { id: string; name: string } | null;
      };
      const transformed: Applicant[] = (applications || []).map((app: ApplicationRow) => {
        const prof = profilesById[app.volunteer_id];
        const shiftIds: string[] = app.selected_shift_ids || [];
        // Applications sent from app builds that predate shifts still carry free-text slots.
        const legacySlots: string[] = app.selected_time_slots || [];
        return {
          id: app.id,
          name: `${prof?.first_name || 'Unknown'} ${prof?.last_name || 'User'}`,
          role: 'Volunteer',
          image: prof?.profile_image || null,
          volunteer_id: app.volunteer_id,
          event_id: app.event_id,
          application_id: app.id,
          departments: app.selected_departments || [],
          shiftIds,
          event: app.event?.name || 'Unknown Event',
          answers: [
            { question: 'Selected Departments', answer: (app.selected_departments || []).join(', ') || 'None' },
            ...(shiftsById.size > 0 || shiftIds.length > 0
              ? [{ question: 'Selected Shifts', answer: describePickedShifts(shiftIds, shiftsById) }]
              : []),
            ...(legacySlots.length > 0 ? [{ question: 'Selected Time Slots', answer: legacySlots.join(', ') }] : []),
            { question: 'Application Date', answer: new Date(app.created_at).toLocaleDateString() },
          ],
        };
      });
      if (requestedEventId !== selectedEventIdRef.current) return;
      setApplicants(transformed);
    } catch {
      if (requestedEventId !== selectedEventIdRef.current) return;
      setApplicants([]);
    } finally {
      if (requestedEventId === selectedEventIdRef.current) setLoadingApplicants(false);
    }
  }, [selectedEventId, userId]);

  useEffect(() => {
    if (activeTab === 'waiting' && selectedEventId) fetchVolunteerApplications();
  }, [activeTab, selectedEventId, fetchVolunteerApplications]);

  const handleAcceptApplicant = useCallback(
    async (applicant: Applicant) => {
      if (!selectedEventId) return;
      try {
        const result = await upsertVolunteerFromApplicant(applicant);
        if (!result.ok) {
          options?.onAcceptError?.(result.message);
          console.error('Accept applicant failed:', result.message);
          return;
        }
        const shiftWarning = await assignApplicantShifts(applicant);
        const { error: delErr } = await supabase.from('volunteer_applications').delete().eq('id', applicant.application_id);
        if (delErr) {
          options?.onAcceptError?.(delErr.message || 'Volunteer was added but removing the application failed');
          console.error('Delete application after accept:', delErr);
        } else if (result.notifyVolunteer) {
          insertVolunteerNotification({
            user_id: applicant.volunteer_id,
            title: "You're in!",
            message: `You've been accepted into "${applicant.event}".`,
            type: 'volunteer_event_accepted',
            data: { event_id: applicant.event_id, event_name: applicant.event },
            read: false,
          });
        }
        if (!delErr && shiftWarning) options?.onAcceptWarning?.(shiftWarning);
        await fetchVolunteerApplications();
        refetch();
      } catch (err) {
        console.error('Error accepting applicant:', err);
        options?.onAcceptError?.(err instanceof Error ? err.message : 'Failed to accept applicant');
      }
    },
    [selectedEventId, fetchVolunteerApplications, refetch, options],
  );

  const handleDenyApplicant = useCallback(
    async (applicant: Applicant) => {
      try {
        await supabase.from('volunteer_applications').delete().eq('id', applicant.application_id);
        await fetchVolunteerApplications();
      } catch (err) {
        console.error('Error denying applicant:', err);
      }
    },
    [fetchVolunteerApplications],
  );

  const handleAcceptAll = useCallback(
    async () => {
      if (!selectedEventId) return;
      const shiftWarnings: string[] = [];
      try {
        for (const applicant of applicants) {
          const result = await upsertVolunteerFromApplicant(applicant);
          if (!result.ok) {
            options?.onAcceptError?.(result.message);
            console.error('Accept all stopped:', result.message);
            return;
          }
          const shiftWarning = await assignApplicantShifts(applicant);
          if (shiftWarning) shiftWarnings.push(shiftWarning);
          const { error: delErr } = await supabase.from('volunteer_applications').delete().eq('id', applicant.application_id);
          if (delErr) {
            options?.onAcceptError?.(delErr.message || 'Could not remove an application');
            return;
          }
          if (result.notifyVolunteer) {
            insertVolunteerNotification({
              user_id: applicant.volunteer_id,
              title: "You're in!",
              message: `You've been accepted into "${applicant.event}".`,
              type: 'volunteer_event_accepted',
              data: { event_id: applicant.event_id, event_name: applicant.event },
              read: false,
            });
          }
        }
        if (shiftWarnings.length === 1) {
          options?.onAcceptWarning?.(shiftWarnings[0]);
        } else if (shiftWarnings.length > 1) {
          options?.onAcceptWarning?.(
            `${shiftWarnings.length} volunteers were accepted without every shift they picked. Check their shifts in the volunteer list.`,
          );
        }
        await fetchVolunteerApplications();
        refetch();
      } catch (err) {
        console.error('Error accepting all:', err);
        options?.onAcceptError?.(err instanceof Error ? err.message : 'Failed to accept all');
      }
    },
    [applicants, selectedEventId, fetchVolunteerApplications, refetch, options],
  );

  return {
    applicants,
    loadingApplicants,
    fetchVolunteerApplications,
    handleAcceptApplicant,
    handleDenyApplicant,
    handleAcceptAll,
  };
}
